import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Activity, 
  MessageCircle, 
  TrendingUp, 
  Clock, 
  Heart,
  ArrowRight,
  Sparkles,
  Plus,
  BarChart3,
  Shield
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCycle } from '../context/CycleContext';
import { useInsights } from '../context/InsightsContext';
import { useNotifications } from '../context/NotificationContext';
import MedicalAlertBanner from '../components/notifications/MedicalAlertBanner';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { stats, logs } = useCycle();
  const { unreadCount } = useInsights();
  const { alerts } = useNotifications();

  const criticalAlertsCount = alerts.filter(alert => 
    alert.severity === 'critical' && !alert.isRead
  ).length;

  const quickActions = [
    {
      title: 'Log Today',
      description: 'Record your cycle data',
      icon: Plus,
      path: '/app/dashboard',
      color: 'from-purple-500 to-pink-500',
      action: 'log'
    },
    {
      title: 'View Calendar',
      description: 'Track your cycle visually',
      icon: Calendar,
      path: '/app/calendar',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Ask AI Doctor',
      description: 'Get instant health advice',
      icon: MessageCircle,
      path: '/app/ask-doctor',
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'View Profile',
      description: 'Manage your settings',
      icon: Shield,
      path: '/app/profile',
      color: 'from-orange-500 to-amber-500'
    }
  ];

  const stats_cards = [
    {
      title: 'Next Period',
      value: stats.nextPeriodPrediction 
        ? (() => {
            const days = Math.ceil((new Date(stats.nextPeriodPrediction).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
            if (days === 0) return 'Today';
            if (days === 1) return 'Tomorrow';
            if (days > 0) return `In ${days} days`;
            return 'Calculating...';
          })()
        : 'No data yet',
      icon: Clock,
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Current Phase',
      value: stats.currentPhase ? t(`dashboard.${stats.currentPhase}`) : 'Unknown',
      subtitle: stats.phaseDay ? `Day ${stats.phaseDay}` : '',
      icon: Activity,
      color: 'from-pink-500 to-rose-500'
    },
    {
      title: 'Cycle Length',
      value: `${stats.averageCycleLength} days`,
      subtitle: 'Average',
      icon: TrendingUp,
      color: 'from-blue-500 to-indigo-500'
    },
    {
      title: 'Tracked Cycles',
      value: logs.filter(log => log.period_start).length.toString(),
      subtitle: 'Total periods',
      icon: Heart,
      color: 'from-green-500 to-emerald-500'
    }
  ];

  const recentActivity = logs.slice(0, 3).map(log => ({
    date: new Date(log.date).toLocaleDateString(),
    description: log.period_start 
      ? 'Period started' 
      : log.symptoms?.length 
        ? `Logged ${log.symptoms.length} symptoms`
        : 'Cycle data logged',
    type: log.period_start ? 'period' : 'log'
  }));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="space-y-8"
    >
      {/* Medical Alert Banner */}
      <MedicalAlertBanner />

      {/* Welcome Section */}
      <motion.div
        className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-pink-500 to-indigo-600 rounded-3xl p-8 text-white"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10">
          <motion.div
            className="flex items-center space-x-2 mb-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Sparkles className="w-6 h-6 text-yellow-300" />
            <span className="text-purple-100">Welcome back!</span>
            {criticalAlertsCount > 0 && (
              <motion.span
                className="px-2 py-1 bg-red-500 text-white text-xs rounded-full font-bold"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                {criticalAlertsCount} Alert{criticalAlertsCount > 1 ? 's' : ''}
              </motion.span>
            )}
          </motion.div>
          
          <motion.h1
            className="text-3xl md:text-4xl font-bold mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Hello, {user?.user_metadata?.full_name || 'there'}! 👋
          </motion.h1>
          
          <motion.p
            className="text-purple-100 text-lg mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Take control of your menstrual health journey with personalized insights and smart notifications.
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Link
              to="/app/dashboard"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-xl font-medium hover:bg-white/30 transition-all duration-300"
            >
              <Plus className="w-5 h-5" />
              <span>Log Today</span>
            </Link>
            
            <Link
              to="/app/calendar"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white text-purple-600 rounded-xl font-medium hover:bg-purple-50 transition-all duration-300"
            >
              <Calendar className="w-5 h-5" />
              <span>View Calendar</span>
            </Link>
          </motion.div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 left-10 w-24 h-24 bg-pink-300/20 rounded-full blur-xl"></div>
      </motion.div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats_cards.map((stat, index) => (
          <motion.div
            key={stat.title}
            className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * (index + 1) }}
            whileHover={{ y: -5, scale: 1.02 }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-500 mb-1">{stat.title}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                {stat.subtitle && (
                  <p className="text-sm text-gray-600">{stat.subtitle}</p>
                )}
              </div>
              <motion.div
                className={`p-3 bg-gradient-to-br ${stat.color} rounded-xl`}
                whileHover={{ rotate: 10, scale: 1.1 }}
              >
                <stat.icon className="w-6 h-6 text-white" />
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => (
            <motion.div
              key={action.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <Link
                to={action.path}
                className="block bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <motion.div
                    className={`p-3 bg-gradient-to-br ${action.color} rounded-xl`}
                    whileHover={{ rotate: 10, scale: 1.1 }}
                  >
                    <action.icon className="w-6 h-6 text-white" />
                  </motion.div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{action.title}</h3>
                <p className="text-gray-600 text-sm">{action.description}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Recent Activity & Health Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Activity */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Recent Activity</h3>
            <Link
              to="/app/dashboard"
              className="text-purple-600 hover:text-purple-700 text-sm font-medium"
            >
              View all
            </Link>
          </div>

          {recentActivity.length > 0 ? (
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <motion.div
                  key={index}
                  className="flex items-center space-x-4 p-3 bg-purple-50/50 rounded-xl"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                >
                  <div className={`p-2 rounded-lg ${
                    activity.type === 'period' 
                      ? 'bg-pink-100 text-pink-600' 
                      : 'bg-purple-100 text-purple-600'
                  }`}>
                    {activity.type === 'period' ? (
                      <Heart className="w-4 h-4" />
                    ) : (
                      <BarChart3 className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                    <p className="text-xs text-gray-500">{activity.date}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No recent activity</p>
              <Link
                to="/app/dashboard"
                className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Start Tracking</span>
              </Link>
            </div>
          )}
        </motion.div>

        {/* Health Insights & Alerts */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Health Insights</h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                  {unreadCount} new
                </span>
              )}
              {criticalAlertsCount > 0 && (
                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                  {criticalAlertsCount} alert{criticalAlertsCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {criticalAlertsCount > 0 && (
              <motion.div
                className="p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
              >
                <div className="flex items-start space-x-3">
                  <motion.div 
                    className="p-2 bg-red-100 rounded-lg"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                  </motion.div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">Health Alert</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      You have {criticalAlertsCount} critical health alert{criticalAlertsCount > 1 ? 's' : ''} that require attention.
                    </p>
                    <Link
                      to="/app/profile"
                      className="inline-flex items-center space-x-1 text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      <span>View alerts</span>
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}

            <motion.div
              className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
            >
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">Smart Notifications</h4>
                  <p className="text-sm text-gray-600">
                    Get personalized alerts for irregular periods, period reminders, and health insights based on your data.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
            >
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <MessageCircle className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">AI Health Assistant</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Have questions about your cycle? Our AI assistant is here to help 24/7.
                  </p>
                  <Link
                    to="/app/ask-doctor"
                    className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    <span>Ask a question</span>
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default HomePage;